import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAppSettings } from '../context/SettingsContext';
import { getActiveDudaAccount } from '../settings/dudaAccounts';
import type { DudaChartMetric } from '../services/dudaApi';
import {
  fetchDudaSiteAnalytics,
  fetchDudaSiteTrafficAnalytics,
  getDudaSiteDetail,
  parseDudaActivitiesBreakdown,
  parseDudaActivityVisits,
  parseDudaSeries,
} from '../services/dudaApi';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  if (value !== index) {
    return null;
  }
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function InlineSpinner() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
      <CircularProgress size={16} />
      <Typography variant="body2" color="text.secondary">
        Loading…
      </Typography>
    </Box>
  );
}

function PreBlock({ data }: { data: unknown }) {
  return (
    <Box
      component="pre"
      sx={{
        mt: 1,
        mb: 0,
        p: 1.25,
        borderRadius: 1,
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
        overflow: 'auto',
        maxHeight: 520,
        fontSize: 12,
      }}
    >
      {JSON.stringify(data, null, 2)}
    </Box>
  );
}

const DudaSiteAnalytics: React.FC = () => {
  const { settings } = useAppSettings();
  const navigate = useNavigate();
  const { siteName } = useParams();

  const dudaAccount = useMemo(() => getActiveDudaAccount(settings), [settings]);
  const { apiUser, apiPassword } = dudaAccount;
  const credsOk = apiUser.trim() && apiPassword.trim();

  const decodedSiteName = useMemo(() => {
    const raw = siteName ?? '';
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }, [siteName]);

  const [tab, setTab] = useState(0);
  const [metric, setMetric] = useState<DudaChartMetric>('visitors');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [siteJson, setSiteJson] = useState<unknown>(null);
  const [activitiesJson, setActivitiesJson] = useState<unknown>(null);
  const [trafficJson, setTrafficJson] = useState<unknown>(null);
  const [pagesJson, setPagesJson] = useState<unknown>(null);

  const load = useCallback(async () => {
    if (!credsOk || !decodedSiteName.trim()) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [site, activities, traffic] = await Promise.all([
        getDudaSiteDetail(apiUser, apiPassword, decodedSiteName),
        fetchDudaSiteAnalytics(apiUser, apiPassword, decodedSiteName, { result: 'activities', dateGranularity: 'DAYS' }),
        fetchDudaSiteTrafficAnalytics(apiUser, apiPassword, decodedSiteName, { dateGranularity: 'DAYS' }),
      ]);
      setSiteJson(site);
      setActivitiesJson(activities);
      setTrafficJson(traffic);

      // Best-effort “pages visited” style data. If the account doesn’t support it,
      // we’ll just show null / error in the Raw tab.
      try {
        const pages = await fetchDudaSiteAnalytics(apiUser, apiPassword, decodedSiteName, {
          result: 'pages',
          dateGranularity: 'DAYS',
        });
        setPagesJson(pages);
      } catch {
        setPagesJson(null);
      }
    } catch (e) {
      const msg = axios.isAxiosError(e)
        ? e.message === 'Network Error'
          ? 'Network error — in dev, run via npm start (proxy). In production, add a backend proxy; browsers cannot call Duda directly due to CORS.'
          : e.response?.data?.message || e.message
        : 'Failed to load site analytics';
      setError(String(msg));
      setSiteJson(null);
      setActivitiesJson(null);
      setTrafficJson(null);
      setPagesJson(null);
    } finally {
      setLoading(false);
    }
  }, [apiPassword, apiUser, credsOk, decodedSiteName]);

  useEffect(() => {
    void load();
  }, [load]);

  const activityTotal = useMemo(() => parseDudaActivityVisits(activitiesJson), [activitiesJson]);
  const seriesTraffic = useMemo(() => parseDudaSeries(trafficJson, metric), [trafficJson, metric]);
  const seriesActivities = useMemo(() => parseDudaSeries(activitiesJson, metric), [activitiesJson, metric]);
  const activityBreakdown = useMemo(() => parseDudaActivitiesBreakdown(activitiesJson), [activitiesJson]);

  const topKpis = useMemo(() => {
    const total = activityTotal;
    const days = Math.max(seriesTraffic.length, seriesActivities.length);
    const last =
      seriesActivities.length > 0
        ? seriesActivities[seriesActivities.length - 1].value
        : seriesTraffic.length > 0
          ? seriesTraffic[seriesTraffic.length - 1].value
          : null;
    return { total, days, last };
  }, [activityTotal, seriesTraffic, seriesActivities]);

  const title = decodedSiteName || 'Duda site';

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight={900}>
            {title}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Duda analytics · {dudaAccount.label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="text" onClick={() => navigate('/duda')}>
            Back to sites
          </Button>
          <Button variant="outlined" onClick={() => void load()} disabled={!credsOk || loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {!credsOk && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Add your Duda API credentials in Settings to load analytics.
        </Alert>
      )}

      {loading && <InlineSpinner />}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={`Metric: ${metric.replace(/_/g, ' ')}`} />
            {topKpis.total != null && <Chip color="primary" label={`Total (best-effort): ${topKpis.total}`} />}
            <Chip variant="outlined" label={`Days: ${topKpis.days}`} />
            {topKpis.last != null && <Chip variant="outlined" label={`Latest: ${topKpis.last}`} />}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Overview" />
            <Tab label="Charts" />
            <Tab label="Traffic (raw)" />
            <Tab label="Pages (best-effort)" />
            <Tab label="Raw JSON" />
          </Tabs>

          <TabPanel value={tab} index={0}>
            <Typography variant="body2" color="text.secondary">
              Site detail
            </Typography>
            <PreBlock data={siteJson} />
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                Parses Duda&apos;s <code>VISITORS</code>, <code>VISITS</code>, <code>PAGE_VIEWS</code>, and activity
                counters from each response (including nested <code>data</code> objects).
              </Typography>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="metric-select">Chart metric</InputLabel>
                <Select
                  labelId="metric-select"
                  value={metric}
                  label="Chart metric"
                  onChange={(e) => setMetric(e.target.value as DudaChartMetric)}
                >
                  <MenuItem value="visitors">Visitors</MenuItem>
                  <MenuItem value="visits">Visits</MenuItem>
                  <MenuItem value="pageviews">Page views</MenuItem>
                  <MenuItem value="form_submits">Form submits</MenuItem>
                  <MenuItem value="click_to_calls">Click to call</MenuItem>
                  <MenuItem value="click_to_maps">Click to map</MenuItem>
                  <MenuItem value="click_to_emails">Click to email</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {seriesTraffic.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Traffic (<code>result=traffic</code>, daily)
                </Typography>
                <Box sx={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={seriesTraffic}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill="#ea580c" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </>
            )}

            {seriesActivities.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Activities (<code>result=activities</code>, daily)
                </Typography>
                <Box sx={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={seriesActivities}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </>
            )}

            {activityBreakdown.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Activity totals (summary object)
                </Typography>
                <Box sx={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={activityBreakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill="#0d9488" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </>
            )}

            {seriesTraffic.length === 0 && seriesActivities.length === 0 && activityBreakdown.length === 0 && (
              <Typography sx={{ mt: 2 }} color="text.secondary">
                No plottable points for this metric — open <strong>Raw JSON</strong> to inspect the response.
              </Typography>
            )}
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Typography variant="body2" color="text.secondary">
              <code>result=traffic</code> (shape varies by account)
            </Typography>
            <PreBlock data={trafficJson} />
          </TabPanel>

          <TabPanel value={tab} index={3}>
            <Typography variant="body2" color="text.secondary">
              Best-effort attempt at “pages visited” style data (not supported on all accounts).
            </Typography>
            <PreBlock data={pagesJson} />
          </TabPanel>

          <TabPanel value={tab} index={4}>
            <Typography variant="subtitle2" gutterBottom>
              Activities
            </Typography>
            <PreBlock data={activitiesJson} />
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Traffic
            </Typography>
            <PreBlock data={trafficJson} />
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Site detail
            </Typography>
            <PreBlock data={siteJson} />
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DudaSiteAnalytics;

